import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import {
  searchCandidateProfileQueryHistoryListSx,
  searchCandidateProfileQueryHistoryPreviewSx
} from 'modules/candidate-profile/styles';
import { useSearchCandidateProfileQueryHistoryList } from 'modules/candidate-profile/hooks/useSearchCandidateProfileQueryHistoryList';
import { useSearchCandidateProfileQueryConversationHistoryActions } from 'modules/candidate-profile/hooks/useSearchCandidateProfileQueryConversationHistoryActions';
import { SearchCandidateProfileQueryHistoryListItems } from './SearchCandidateProfileQueryHistoryListItems';
import {
  HandleSelectSearchCandidateProfileQueryProps,
  useSelectSearchCandidateProfileQuery
} from 'modules/candidate-profile/hooks/useSelectSearchCandidateProfileQuery';

const PINNED_LIMIT = 3;
const RECENT_LIMIT = 5;

const PreviewSectionHeader: React.FC<{
  label: string;
  onViewAll?: () => void;
}> = ({ label, onViewAll }) => (
  <Box sx={searchCandidateProfileQueryHistoryPreviewSx.sectionTopRow}>
    <Typography sx={searchCandidateProfileQueryHistoryPreviewSx.sectionHeader}>
      {label}
    </Typography>
    {onViewAll && (
      <Link
        component="button"
        onClick={onViewAll}
        sx={searchCandidateProfileQueryHistoryPreviewSx.viewAllLink}
      >
        View all
      </Link>
    )}
  </Box>
);

type SearchCandidateProfileQueryHistoryPreviewProps = {
  onSelectSearchCandidateProfileQuery: (
    data: HandleSelectSearchCandidateProfileQueryProps
  ) => void;
  onViewAll: () => void;
  onDeleteSearchCandidateProfileQuery: (id: string) => void;
};

export const SearchCandidateProfileQueryHistoryPreview: React.FC<
  SearchCandidateProfileQueryHistoryPreviewProps
> = ({
  onSelectSearchCandidateProfileQuery,
  onViewAll,
  onDeleteSearchCandidateProfileQuery
}) => {
  const {
    pinnedList,
    recentList,
    updatePinStatus,
    updateDisplayTitle,
    removeItem
  } = useSearchCandidateProfileQueryHistoryList();

  const { getMenuOptions } =
    useSearchCandidateProfileQueryConversationHistoryActions({
      updatePinStatus,
      updateDisplayTitle,
      removeItem,
      onDelete: onDeleteSearchCandidateProfileQuery
    });

  const { handleItemClick } = useSelectSearchCandidateProfileQuery({
    onSelectSearchCandidateProfileQuery
  });

  const pinned = pinnedList.slice(0, PINNED_LIMIT);
  const recent = recentList.slice(0, RECENT_LIMIT);

  if (pinned.length === 0 && recent.length === 0) {
    return null;
  }

  return (
    <Box sx={searchCandidateProfileQueryHistoryPreviewSx.container}>
      {pinned.length > 0 && (
        <Box sx={searchCandidateProfileQueryHistoryListSx.section}>
          <PreviewSectionHeader
            label="Pinned searches"
            onViewAll={recent.length === 0 ? onViewAll : undefined}
          />
          <SearchCandidateProfileQueryHistoryListItems
            items={pinned}
            getMenuOptions={getMenuOptions}
            onItemClick={handleItemClick}
          />
        </Box>
      )}

      {recent.length > 0 && (
        <Box sx={searchCandidateProfileQueryHistoryListSx.section}>
          <PreviewSectionHeader label="Recent searches" onViewAll={onViewAll} />
          <SearchCandidateProfileQueryHistoryListItems
            items={recent}
            getMenuOptions={getMenuOptions}
            onItemClick={handleItemClick}
          />
        </Box>
      )}
    </Box>
  );
};
